import AuthApi from '@/api/auth'
import shopApi from '@/api/shop'
import { ShopSettingsCategory } from '@/api/types/shop'
import { shopInfoAtom, shopSettingAtom } from '@/atom/shop-info'
import { CHANNELWILL_APP_ID } from '@/constants/app'
import { GLOBAL_API_ERROR, TOKEN_KEY } from '@/constants/keys'
import { delayOpen } from '@/lib/auth/delayOpen'
import { checkExpired } from '@/lib/auth/jwt'
import { setSnapshot } from '@/lib/auth/pubsub'
import { initInChannelwill } from '@/lib/channelwill/init'
import { errorBridge } from '@/lib/error-bridge-state'
import { keepState, tryGetState } from '@/lib/keepState'
import { $env } from '@/lib/location-env'
import { isEmbedded } from '@/lib/shopify-bridge'
import storageScheme from '@/lib/storage-scheme'
import { channelwill } from '@channelwill/sdk'
import { getDefaultStore } from 'jotai'

class TokenExpiredError extends Error {
  code = '50X'
  message = 'Token Expired'
}

const oAuthState = {
  authenticated: false,
}
const openTarget = isEmbedded() ? '_top' : '_self'
const globalAtomStore = getDefaultStore()
const urlSearch = new URLSearchParams(location.search)
let hmac = urlSearch.get('hmac')
let shop = urlSearch.get('shop')
let timestamp = urlSearch.get('timestamp')
let host = urlSearch.get('host')
let state = urlSearch.get('state')
let urlSearchString = location.search

const shopifyAppQueryKeys = [
  'hmac',
  'shop',
  'timestamp',
  'host',
  'state',
  'urlSearch',
]
function iframeKeepAlive() {
  if (!hmac) {
    const oldState = tryGetState(shopifyAppQueryKeys)
    if (oldState) {
      hmac = oldState.hmac
      shop = oldState.shop
      timestamp = oldState.timestamp
      host = oldState.host
      state = oldState.state
      urlSearchString = oldState.urlSearch
    }
  } else {
    keepState({
      hmac,
      shop,
      timestamp,
      host,
      state,
      urlSearch: location.search,
    })
  }
}
iframeKeepAlive()

/**
 * @description 是否重新登录成功，否则需要重定向
 */
async function reAuth(): Promise<boolean> {
  if (!shop) return false

  storageScheme.setItem('shopName', shop)

  const res = await AuthApi.authUser(urlSearchString)

  // 上报authUser接口返回的关键信息
  const { data } = res

  storageScheme.setItem('authUserInfo', JSON.stringify(data))

  if (data.status === 1) {
    // 重定向去安装
    open(data.url, openTarget)
    return false
  }

  if (data.status === 2) {
    // 进入APP, 由于这里更新了本地存储，为了避免保存失败，后面需要延迟跳转
    storageScheme.setItem(TOKEN_KEY, data.token)
    // 新手引导完成状态
    storageScheme.setItem('completedGuide', data.completed_guide, 'session')

    // 判断本页面是否由code为500的请求跳转而来的，如果是，则获取完信息之后，跳转回去
    const nowRoute = storageScheme.getItem('nowRoute')
    if (nowRoute) {
      storageScheme.removeItem('nowRoute')
      delayOpen(nowRoute, openTarget)
      return false
    }

    // 判断本页面是否由用户从书签进入（无token直接进入页面）
    const firstRoute = storageScheme.getItem('firstRoute')
    if (firstRoute) {
      storageScheme.removeItem('firstRoute')
      delayOpen(firstRoute, openTarget)
      return false
    }
    //  else {
    //   open('/', '_top')
    // }

    // 如果不是嵌入模式，直接跳转到对应的页面
    if (!isEmbedded() && (host || shop)) {
      delayOpen(
        `https://${host ? atob(host) : `${shop}/admin`}/apps/${
          process.env.PUBLIC_SHOPIFY_API_KEY
        }`,
        openTarget,
      )
      return false
    }
    return true
  }

  if (data.status === 3) {
    // 权限不足需要重装
    open(data.url, openTarget)
    return false
  }

  return false
}

/**
 * 是否已经登录
 */
async function checkAuth(): Promise<boolean> {
  if (channelwill.isEmbedded()) {
    const token = await getChannelAppToken()
    storageScheme.setItem(TOKEN_KEY, token)
    const done = await getShopData()
    return done
  }

  if ($env.localhost) {
    storageScheme.setItem(TOKEN_KEY, shop ?? 'loloyal-checkout.myshopify.com')
    const done = await getShopData()
    return done
  }

  if ($env.dashboard) {
    storageScheme.setItem(TOKEN_KEY, $env.dashboardToken)
    const done = await getShopData()
    return done
  }

  const shopToken = storageScheme.getItem(TOKEN_KEY)

  // NewUser
  if (
    !shopToken ||
    checkExpired(shopToken) ||
    (shop && hmac && timestamp && !isEmbedded())
  ) {
    const reAuthDone = await reAuth()
    if (!reAuthDone) return reAuthDone
  }

  // OldUser
  try {
    const done = await getShopData()
    return done
  } catch (error) {
    if (!(error instanceof TokenExpiredError)) {
      throw error
    }

    // Token过期
    const authDone = await reAuth()
    if (!authDone) return authDone

    const done = await getShopData()
    return done
  }
}

async function reportErrorToDingTalk(obj: any) {
  const raw = JSON.stringify(obj, null, 2)
  const message = [
    `\`\`\`json\n${raw}\n\`\`\``,
    `> **Shop**: ${shop}`,
    `> **Timestamp**: ${timestamp}`,
    `> **UserAgent**: ${navigator.userAgent}`,
  ].join('\n')
  await shopApi.reportError({
    reportData: message,
  })
}
// 提取获取数据的逻辑
async function fetchShopDataAndSettings() {
  const [shopDataRes, shopSettingsRes] = await Promise.all([
    shopApi.getShopData(),
    shopApi.getShopSetting(ShopSettingsCategory.SMART_TAG),
  ])

  globalAtomStore.set(shopInfoAtom, shopDataRes.data)
  globalAtomStore.set(shopSettingAtom, shopSettingsRes.data)

  return shopDataRes.data
}

// 提取重试逻辑
async function fetchWithRetry() {
  let firstError = null

  try {
    return await fetchShopDataAndSettings()
  } catch (error) {
    firstError = error
    console.warn('First attempt failed, retrying in 3 seconds...', error)
  }

  // 重试逻辑
  try {
    await new Promise((resolve) => setTimeout(resolve, 1000))
    const shopInfo = await fetchShopDataAndSettings()

    // 上报第一次错误但重试成功
    await reportErrorToDingTalk([firstError, 'Retry successful, no error'])
    return shopInfo
  } catch (retryError) {
    // 两次都失败了
    if (retryError != null) {
      throw new TokenExpiredError()
    }
    await reportErrorToDingTalk([firstError, retryError])
    throw retryError
  }
}

export async function getShopData() {
  let shopInfo = globalAtomStore.get(shopInfoAtom)
  let shopSettings = globalAtomStore.get(shopSettingAtom)

  if (!shopInfo || !shopSettings) {
    shopInfo = await fetchWithRetry()
  }

  const uninstalled = shopInfo?.userInfo.is_delete === 1 /* 该店铺已卸载 */

  if (!$env.dashboard && uninstalled) {
    console.warn(
      'This shop has been uninstalled, ' + shopInfo?.userInfo.shop_name,
    )
    throw new TokenExpiredError()
  }

  return true
}

async function getToken() {
  if ($env.dashboard) {
    return storageScheme.getItem(TOKEN_KEY) || storageScheme.getItem('shopName')
  }
  if (channelwill.isEmbedded()) {
    const token = await getChannelAppToken()
    return token
  }
  return storageScheme.getItem(TOKEN_KEY)
}

async function getChannelAppToken() {
  const token = await channelwill.idToken(CHANNELWILL_APP_ID)
  return `Bearer ${token}`
}

async function init() {
  initInChannelwill()

  await checkAuth()
    .then((done) => {
      if (done) {
        setSnapshot(true)
      }
    })
    .catch((error) => {
      errorBridge.show(GLOBAL_API_ERROR, error.message)
      setSnapshot(true)
    })
}

export default {
  init,
  getToken,
  checkAuth,
  reAuth,
  state: oAuthState,
}
